import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import { Theme } from "@mui/material/styles";

import { useEffect, useState } from "react";
import style from "./contenderFormControl.module.css";

type Props = {
  contenders: Contender[];
  onChange: (contenders: Contender[]) => void;
};

export type Contender = {
  name: string;
  description?: string;
  imageFile?: File;
  imagePreview?: string;
};

export default function ContenderFormControl({ contenders, onChange }: Props) {
  const [showModal, setShowModal] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleRemoveContender = (index: number) => {
    const updated = contenders.filter((_, i) => i !== index);
    onChange(updated);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        // This doesn't work, need to work out alternative
        e.stopImmediatePropagation();
        setShowModal(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const onDialogClose = () => {
    setShowModal(false);
    setNameError(false);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleImageFile = (file: File) => {
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        const file = items[i].getAsFile();
        if (file) {
          handleImageFile(file);
          e.preventDefault();
          break;
        }
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      handleImageFile(file);
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find((t) => t.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], "pasted-image.png", { type: imageType });
          handleImageFile(file);
          return;
        }
      }
    } catch (err) {
      console.warn("Direct clipboard read permission denied or unsupported:", err);
    }
  };

  const onDialogSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const formJson = Object.fromEntries(formData.entries()) as { name: string; description: string };
    
    if (!formJson.name.trim()) {
      setNameError(true);
      return;
    }

    onChange([...contenders, { 
      name: formJson.name, 
      description: formJson.description,
      imageFile: selectedImage || undefined,
      imagePreview: imagePreview || undefined,
    }]);
    setShowModal(false);
    setNameError(false);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const renderListItems = () => {
    return contenders.map((c, i) => (
      <ListItem
        key={i}
        secondaryAction={
          <IconButton 
            edge="end" 
            aria-label="delete" 
            onClick={() => handleRemoveContender(i)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        }
      >
        {c.imagePreview && (
          <ListItemAvatar>
            <Avatar 
              src={c.imagePreview} 
              alt={c.name}
              variant="rounded"
              sx={{ width: 48, height: 48, mr: 1 }}
            />
          </ListItemAvatar>
        )}
        <ListItemText 
          primary={c.name} 
          secondary={c.description}
        />
      </ListItem>
    ));
  };

  return (
    <>
      <div className={style.btn}>
        <Button variant="contained" onClick={() => setShowModal(true)}>
          Add
        </Button>
      </div>

      {contenders.length > 0 && (
        <div className={style.list}>
          <List>{renderListItems()}</List>
        </div>
      )}

      <Dialog
        open={showModal}
        onClose={onDialogClose}
        onPaste={handlePaste}
        PaperProps={{
          component: "form",
          onSubmit: onDialogSubmit,
        }}
        disableRestoreFocus
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Add Contender
          <IconButton
            aria-label="close"
            onClick={onDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme: Theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField 
            label="Name" 
            name="name" 
            autoFocus 
            fullWidth 
            required
            error={nameError}
            helperText={nameError ? "Name is required" : ""}
            sx={{ mt: 1 }} 
            onChange={() => setNameError(false)}
          />
          <TextField 
            label="Description (Optional)" 
            name="description" 
            fullWidth 
            multiline
            rows={3}
            sx={{ mt: 2 }} 
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" mb={1}>
              Image (Optional)
            </Typography>
            <Box
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCameraIcon />}
                color="secondary"
                fullWidth
                sx={{ py: 1.5, borderStyle: 'dashed' }}
              >
                {selectedImage ? selectedImage.name : "Choose or Drag & Drop Image"}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageSelect}
                />
              </Button>
            </Box>
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
              <Typography variant="caption" color="textSecondary">
                💡 Tip: You can paste an image with <strong>Ctrl+V</strong> / <strong>Cmd+V</strong>
              </Typography>
              {navigator.clipboard && 'read' in navigator.clipboard && (
                <Button
                  size="small"
                  variant="text"
                  color="secondary"
                  onClick={handlePasteFromClipboard}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Paste from Clipboard
                </Button>
              )}
            </Box>

            {imagePreview && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, objectFit: 'cover' }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                  sx={{ mt: 0.5 }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button type="submit" variant="contained" color="secondary" sx={{ px: 4 }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
